import { Button } from '@/components/ui/button'
import { Drawer, DrawerContentRight } from '@/components/ui/drawer'
import { useSettingsPanelState } from '@/hooks/useSettingsPanelState'
import { usePhoneNumbers } from '@/queries/phone-numbers'
import { useStytchOrganization } from '@stytch/react/b2b'
import type { PhoneNumber } from 'honeygrid-types'
import { LayoutGrid, Plus, Table2 } from 'lucide-react'

import { PhoneNumberRecordPanel } from './PhoneNumberRecordPanel'
import { PhoneNumbersCards } from './PhoneNumbersCards'
import { PhoneNumbersTable } from './PhoneNumbersTable'

export function PhoneNumbersList() {
	const { state, openCreate, openRecord, closePanel, setView } =
		useSettingsPanelState('phone-numbers')
	const { organization } = useStytchOrganization()

	// Fetch phone numbers from API
	const { data: phoneNumbers = [], isLoading } = usePhoneNumbers(organization?.organization_id)

	const selectedPhoneNumber = state.recordId
		? phoneNumbers.find((p) => p.uuid === state.recordId)
		: undefined

	const handleAdd = () => openCreate()
	const handleSelect = (phoneNumber: PhoneNumber) => openRecord(phoneNumber.uuid)

	const listPanel = (
		<div className="flex flex-col h-full">
			<div className="flex items-center justify-between mb-4">
				<div className="text-lg font-semibold">Phone Numbers</div>
				<div className="flex items-center gap-2">
					<div className="flex rounded-md border border-[#3A2E2F]/20">
						<Button
							variant={state.view === 'table' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('table')}
							className="rounded-r-none"
						>
							<Table2 className="h-4 w-4" />
						</Button>
						<Button
							variant={state.view === 'cards' ? 'default' : 'ghost'}
							size="sm"
							onClick={() => setView('cards')}
							className="rounded-l-none"
						>
							<LayoutGrid className="h-4 w-4" />
						</Button>
					</div>
					<Button onClick={handleAdd} size="sm" className="cursor-pointer">
						<Plus className="h-4 w-4 mr-2" />
						Add Phone Number
					</Button>
				</div>
			</div>
			<div className="flex-1 overflow-auto">
				{isLoading ? (
					<div className="flex items-center justify-center h-32">
						<p className="text-sm text-muted-foreground">Loading phone numbers...</p>
					</div>
				) : state.view === 'table' ? (
					<PhoneNumbersTable
						phoneNumbers={phoneNumbers}
						onSelect={handleSelect}
						onAdd={handleAdd}
						selectedId={state.recordId || undefined}
					/>
				) : (
					<PhoneNumbersCards
						phoneNumbers={phoneNumbers}
						onSelect={handleSelect}
						onAdd={handleAdd}
						selectedId={state.recordId || undefined}
					/>
				)}
			</div>
		</div>
	)

	return (
		<div className="flex flex-col h-full">
			{listPanel}
			<Drawer open={state.isOpen} onOpenChange={(open) => !open && closePanel()} direction="right">
				<DrawerContentRight>
					<PhoneNumberRecordPanel
						onClose={() => closePanel()}
						phoneNumber={selectedPhoneNumber}
						isNew={state.mode === 'new'}
					/>
				</DrawerContentRight>
			</Drawer>
		</div>
	)
}
